import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { fetchTocNodes, Node } from '../../../../api/table-of-content';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { getNodeDescription } from '../../../../utils/getNodeDescription';
import { MaterialDataContext } from '../../_contexts/MaterialDataProvider';
import ModuleContext from '../../_contexts/ModuleContext';
import { findDeepNodeById } from '../../../../utils/tocNode';

export type TableOfContentsContextValue = {
  nodes: Node[];
  selectedNode: Node | undefined;
};

const TableOfContentsContext = createContext<TableOfContentsContextValue>({
  nodes: [],
  selectedNode: undefined,
});

export default TableOfContentsContext;

export function buildNodeParentsById(toc: Node[]) {
  return toc.reduce((parentsMap, node) => {
    const getParents = (n: Node, parents: string[]): void => {
      if (!parentsMap[n.id]) {
        parentsMap[n.id] = parents;
      }

      parents = [...parents, n.id];

      if (n.nodes) {
        n.nodes.forEach(childNode => getParents(childNode, parents));
      }
    };

    getParents(node, []);

    return parentsMap;
  }, {} as Record<string, string[]>);
}

export function TableOfContentsContextProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const { selectedModuleSlug, nodeId } = useParams();

  const { findBySlug } = useContext(ModuleContext);
  const { selectedNodeIds, setSelectedNodeIds } = useContext(MaterialDataContext);

  const [nodes, setNodes] = useState<Node[]>([]);

  const module = useMemo(() => findBySlug(selectedModuleSlug), [findBySlug, selectedModuleSlug]);

  const nodeParentsById = useMemo(() => buildNodeParentsById(nodes), [nodes]);

  const selectedNodeId = selectedNodeIds[selectedNodeIds.length - 1];

  const selectedNode = useMemo(() => {
    const node = findDeepNodeById(nodes, selectedNodeId);

    if (node) return { ...node, description: getNodeDescription(node, t) };
  }, [nodes, selectedNodeId, t]);

  useEffect(() => {
    if (Object.keys(nodeParentsById).length === 0) return;
    if (!nodeId) return setSelectedNodeIds([]);

    setSelectedNodeIds([...nodeParentsById[nodeId], nodeId]);
  }, [nodeParentsById, setSelectedNodeIds, nodeId]);

  const getNodes = useCallback(async () => {
    try {
      if (!module) return;

      const { data: tocNodes } = await fetchTocNodes(module.id);
      setNodes(tocNodes.map(node => ({ ...node, description: getNodeDescription(node, t) })));
    } catch (e) {
      handleError(e);
    }
  }, [module, handleError, t]);

  useEffect(() => {
    getNodes();
  }, [module, handleError, t, getNodes]);

  return (
    <TableOfContentsContext.Provider
      value={{
        nodes,
        selectedNode,
      }}
    >
      {children}
    </TableOfContentsContext.Provider>
  );
}
